/**
 * React Hook for Analytics Tracking
 * Provides easy integration with the AnalyticsService
 */

import { useCallback, useEffect, useRef } from 'react';
import { analyticsService, SearchHistoryEntry, RecentlyVisitedCategory, RecentlyVisitedProduct } from '@/polymet/services/analytics-service';
import { supabase } from '@/lib/supabase';

interface UseAnalyticsOptions {
  enableAutoTracking?: boolean;
  sessionId?: string;
  deviceType?: 'mobile' | 'tablet' | 'desktop';
}

interface UseAnalyticsReturn {
  // Category tracking
  trackCategoryVisit: (params: {
    categoryId?: string;
    subcategoryId?: string;
    productCategoryId?: string;
    visitType: 'category' | 'subcategory' | 'product_category';
    source?: 'search' | 'navigation' | 'recommendation' | 'direct';
    timeSpent?: number;
  }) => Promise<void>;

  // Product tracking
  trackProductVisit: (params: {
    productId: string;
    supplierId?: string;
    categoryId?: string;
    subcategoryId?: string;
    productCategoryId?: string;
    source?: 'search' | 'category' | 'recommendation' | 'direct' | 'rfq' | 'contact';
    timeSpent?: number;
    interactionType?: 'view' | 'contact' | 'rfq' | 'share' | 'favorite';
  }) => Promise<void>;

  // Search tracking
  trackSearch: (params: {
    query: string;
    resultsCount: number;
    filters?: Record<string, any>;
    categoryClicked?: string;
    resultClicked?: boolean;
    searchSource?: 'homepage' | 'header' | 'category_page' | 'product_page';
  }) => Promise<void>;

  // Data retrieval
  getUserSearchHistory: (limit?: number) => Promise<SearchHistoryEntry[]>;
  getUserRecentlyVisited: (limit?: number) => Promise<RecentlyVisitedCategory[]>;
  getUserRecentlyVisitedProducts: (limit?: number) => Promise<RecentlyVisitedProduct[]>;
}

// Detect device type from user agent
function detectDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('mobile') || userAgent.includes('android') || userAgent.includes('iphone')) {
    return 'mobile';
  }
  if (userAgent.includes('tablet') || userAgent.includes('ipad')) {
    return 'tablet';
  }
  return 'desktop';
}

// Generate session ID
function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// Get current user ID
async function getCurrentUserId(): Promise<string | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user?.id) {
      return null;
    }

    // Get the account ID that corresponds to this auth user
    // The analytics tables reference the accounts table, not auth.users
    const { data: account, error } = await supabase
      .from('accounts')
      .select('id')
      .eq('id', user.id) // Assuming accounts.id matches auth.users.id
      .single();

    if (error) {
      console.error('Error fetching account for analytics:', error);
      // Fallback to auth user ID if account lookup fails
      return user.id;
    }

    return account?.id || user.id;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
}

export function useAnalytics(options: UseAnalyticsOptions = {}): UseAnalyticsReturn {
  const sessionIdRef = useRef<string>(options.sessionId || generateSessionId());
  const deviceType = options.deviceType || detectDeviceType();
  
  // Track category visit
  const trackCategoryVisit = useCallback(async (params: {
    categoryId?: string;
    subcategoryId?: string;
    productCategoryId?: string;
    visitType: 'category' | 'subcategory' | 'product_category';
    source?: 'search' | 'navigation' | 'recommendation' | 'direct';
    timeSpent?: number;
  }) => {
    const userId = await getCurrentUserId();
    if (!userId) {
      console.warn('No user ID available for analytics tracking');
      return;
    }

    try {
      await analyticsService.recordCategoryVisit({
        userId,
        categoryId: params.categoryId,
        subcategoryId: params.subcategoryId,
        productCategoryId: params.productCategoryId,
        visitType: params.visitType,
        source: params.source || 'direct',
        sessionId: sessionIdRef.current,
        deviceType,
        timeSpent: params.timeSpent
      });
    } catch (error) {
      console.error('Error tracking category visit:', error);
    }
  }, [deviceType]);

  // Track product visit
  const trackProductVisit = useCallback(async (params: {
    productId: string;
    supplierId?: string;
    categoryId?: string;
    subcategoryId?: string;
    productCategoryId?: string;
    source?: 'search' | 'category' | 'recommendation' | 'direct' | 'rfq' | 'contact';
    timeSpent?: number;
    interactionType?: 'view' | 'contact' | 'rfq' | 'share' | 'favorite';
  }) => {
    const userId = await getCurrentUserId();
    if (!userId) {
      console.warn('No user ID available for product tracking');
      return;
    }

    try {
      await analyticsService.recordProductVisit({
        userId,
        productId: params.productId,
        supplierId: params.supplierId,
        categoryId: params.categoryId,
        subcategoryId: params.subcategoryId,
        productCategoryId: params.productCategoryId,
        source: params.source || 'direct',
        timeSpent: params.timeSpent,
        interactionType: params.interactionType || 'view'
      });
    } catch (error) {
      console.error('Error tracking product visit:', error);
    }
  }, []);

  // Track search
  const trackSearch = useCallback(async (params: {
    query: string;
    resultsCount: number;
    filters?: Record<string, any>;
    categoryClicked?: string;
    resultClicked?: boolean;
    searchSource?: 'homepage' | 'header' | 'category_page' | 'product_page';
  }) => {
    const userId = await getCurrentUserId();
    if (!userId) {
      console.warn('No user ID available for search tracking');
      return;
    }

    try {
      await analyticsService.recordSearch({
        userId,
        query: params.query,
        resultsCount: params.resultsCount,
        filters: params.filters,
        categoryClicked: params.categoryClicked,
        resultClicked: params.resultClicked,
        searchSource: params.searchSource || 'homepage'
      });
    } catch (error) {
      console.error('Error tracking search:', error);
    }
  }, []);

  // Get user search history
  const getUserSearchHistory = useCallback(async (limit: number = 10): Promise<SearchHistoryEntry[]> => {
    const userId = await getCurrentUserId();
    if (!userId) {
      return [];
    }

    try {
      return await analyticsService.getUserSearchHistory(userId, limit);
    } catch (error) {
      console.error('Error fetching search history:', error);
      return [];
    }
  }, []);

  // Get user recently visited categories
  const getUserRecentlyVisited = useCallback(async (limit: number = 10): Promise<RecentlyVisitedCategory[]> => {
    const userId = await getCurrentUserId();
    if (!userId) {
      return [];
    }

    try {
      return await analyticsService.getUserRecentlyVisitedCategories(userId, limit);
    } catch (error) {
      console.error('Error fetching recently visited:', error);
      return [];
    }
  }, []);

  // Get user recently visited products
  const getUserRecentlyVisitedProducts = useCallback(async (limit: number = 10): Promise<RecentlyVisitedProduct[]> => {
    const userId = await getCurrentUserId();
    if (!userId) {
      return [];
    }

    try {
      return await analyticsService.getUserRecentlyVisitedProducts(userId, limit);
    } catch (error) {
      console.error('Error fetching recently visited products:', error);
      return [];
    }
  }, []);

  return {
    trackCategoryVisit,
    trackProductVisit,
    trackSearch,
    getUserSearchHistory,
    getUserRecentlyVisited,
    getUserRecentlyVisitedProducts
  };
}

/**
 * Hook for automatic page visit tracking
 * Automatically tracks when a component mounts
 */
export function usePageVisitTracker(params: {
  categoryId?: string;
  subcategoryId?: string;
  productCategoryId?: string;
  visitType: 'category' | 'subcategory' | 'product_category';
  source?: 'search' | 'navigation' | 'recommendation' | 'direct';
  enabled?: boolean;
}) {
  const { trackCategoryVisit } = useAnalytics();
  const trackedRef = useRef(false);
  const startTimeRef = useRef(Date.now());

  useEffect(() => {
    if (params.enabled === false || trackedRef.current) {
      return;
    }

    trackedRef.current = true;
    
    // Track the visit
    trackCategoryVisit({
      categoryId: params.categoryId,
      subcategoryId: params.subcategoryId,
      productCategoryId: params.productCategoryId,
      visitType: params.visitType,
      source: params.source || 'navigation'
    });

    // Track time spent when component unmounts
    return () => {
      const timeSpent = Math.floor((Date.now() - startTimeRef.current) / 1000);
      
      // Only track if user spent more than 5 seconds on page
      if (timeSpent > 5) {
        trackCategoryVisit({
          categoryId: params.categoryId,
          subcategoryId: params.subcategoryId,
          productCategoryId: params.productCategoryId,
          visitType: params.visitType,
          source: params.source || 'navigation',
          timeSpent
        });
      }
    };
  }, [params.categoryId, params.subcategoryId, params.productCategoryId, params.visitType, params.source, params.enabled, trackCategoryVisit]);
} 